import { useState } from 'react';
import {
  AppBar, Button, Container, Toolbar, Typography,
  Grid,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import './index.css';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import TopNavLogo from './TopNavLogo';
import { straightFont, primary } from '../../Constants';
import AccountMenu from './AccountMenu';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../providers/ModalProvider';
import { useTheme } from '../../providers/ThemeProvider';
import SearchEmblem from '../../components/SearchEmblem';
import SearchBar from '../../components/SearchBar';

export default function TopNav() {
  const { currentUser } = useAuth();
  const { setShowLoginModal } = useModal();
  const { mode, toggleTheme, colors } = useTheme();
  const url = useLocation().pathname;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        background: colors.navBackground,
        zIndex: 9998,
        justifyContent: 'center',
        height: '90px',
        boxShadow: mode === 'light' ? '0 1px 3px rgba(0, 0, 0, 0.08)' : '0 1px 3px rgba(0, 0, 0, 0.3)',
      }}
    >
      <Container
        maxWidth="lg"
      >
        <Toolbar disableGutters sx={{ zIndex: 9998, padding: 0 }}>
          <Grid // TopNav Mobile screen
            container
            sx={{
              display: {
                xs: 'flex',
                md: 'none',
              },
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 0,
            }}
          >
            <Grid item xs="auto">
              <Button
                id="logoButton"
                data-testid="logoButton"
                component={Link}
                to="/home"
              >
                <TopNavLogo size="small" />
              </Button>
            </Grid>

            <Grid item xs={8} sx={{ display: 'flex', justifyContent: 'center' }}>
              <SearchBar />
            </Grid>

            <Grid item xs="auto">
              <IconButton
                onClick={handleClick}
                sx={{ color: colors.text }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                sx={{
                  zIndex: 9999,
                  '& .MuiPaper-root': {
                    backgroundColor: colors.surface,
                    boxShadow: mode === 'light' ? '0 4px 20px rgba(0, 0, 0, 0.1)' : '0 4px 20px rgba(0, 0, 0, 0.4)',
                    borderRadius: 2,
                  },
                }}
              >
                <MenuItem component={Link} to="/home" onClick={handleClose} sx={{ gap: 1 }}>
                  <HomeIcon sx={{ color: colors.text }} />
                  <Typography fontFamily={straightFont} color={colors.text} fontWeight={500}>
                    Home
                  </Typography>
                </MenuItem>
                <Divider sx={{ borderColor: colors.border }} />
                <MenuItem component={Link} to="/recipes" onClick={handleClose} sx={{ gap: 1 }}>
                  <RestaurantMenuIcon sx={{ color: colors.text }} />
                  <Typography fontFamily={straightFont} color={colors.text} fontWeight={500}>
                    Recipes
                  </Typography>
                </MenuItem>
                <Divider sx={{ borderColor: colors.border }} />
                <MenuItem onClick={toggleTheme} sx={{ gap: 1 }}>
                  {mode === 'light' ? (
                    <DarkModeIcon sx={{ color: colors.text }} />
                  ) : (
                    <LightModeIcon sx={{ color: colors.text }} />
                  )}
                  <Typography fontFamily={straightFont} color={colors.text} fontWeight={500}>
                    {mode === 'light' ? 'Dark Mode' : 'Light Mode'}
                  </Typography>
                </MenuItem>
                <Divider sx={{ borderColor: colors.border }} />
                <MenuItem>
                  {currentUser != null ? (
                    <>
                      <AccountMenu />
                      <Typography fontFamily={straightFont} color={colors.text} fontWeight={500}>
                        Profile
                      </Typography>
                    </>
                  ) : (
                    <Button onClick={handleLoginClick}>
                      <Typography
                        sx={{
                          fontFamily: straightFont,
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: primary,
                        }}
                      >
                        Login
                      </Typography>
                    </Button>
                  )}
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
          <Grid // TopNav Normal screen
            container
            sx={{
              display: {
                xs: 'none',
                md: 'flex',
              }, // make this none for small screens
              justifyContent: 'space-between',
              padding: 0,
            }}
          >
            <Grid
              item
              xs={1}
              sm={4}
              sx={{
                display: {
                  xs: 'none',
                  md: 'flex',
                },
                justifyContent: 'left',
                alignItems: 'center',
                padding: 0,
              }}
            >
              <Button
                id="logoButton"
                data-testid="logoButton"
                component={Link}
                to="/home"
              >

                <TopNavLogo size="large" />
              </Button>
            </Grid>
            <Grid
              item
              xs={1}
              sm={4}
              sx={{
                display: {
                  xs: 'flex',
                  md: 'none',
                },
                justifyContent: 'left',
                alignItems: 'center',
                padding: 0,
              }}
            >
              <Button
                id="logoButton"
                data-testid="logoButton"
                component={Link}
                to="/home"
              >

                <TopNavLogo size="small" />
              </Button>
            </Grid>

            <Grid
              item
              xs={11}
              sm={8}
              sx={{
                display: 'flex',
                justifyContent: 'right',
                alignItems: 'center',
                padding: 0,
              }}
            >

              {url !== '/home' && (
                <Button component={Link} to="/home">
                  <Typography
                    fontFamily={straightFont}
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      textTransform: 'none',
                    }}
                    color={colors.text}
                  >
                    Home
                  </Typography>
                </Button>
              )}
              {url !== '/recipes' && (
                <Button id="recipesButton" component={Link} to="/recipes">
                  <Typography
                    fontFamily={straightFont}
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      textTransform: 'none',
                    }}
                    color={colors.text}
                  >
                    Recipes
                  </Typography>
                </Button>
              )}

              <SearchEmblem />

              <Tooltip
                title={mode === 'light' ? 'Dark mode' : 'Light mode'}
                slotProps={{
                  popper: {
                    sx: { zIndex: 99999 },
                  },
                }}
              >
                <IconButton onClick={toggleTheme} sx={{ color: colors.text, ml: 0.5 }}>
                  {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
                </IconButton>
              </Tooltip>

              {currentUser != null ? (
                <AccountMenu />
              ) : (
                <Button onClick={handleLoginClick}>
                  <Typography
                    sx={{
                      fontFamily: straightFont,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      color: primary,
                    }}
                  >
                    Login
                  </Typography>
                </Button>
              )}
            </Grid>
          </Grid>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
